/**
 * The shape of the money that never reaches a member of Congress.
 *
 * The page under this holds a thousand rows and three totals, and neither a
 * thousand rows nor three totals is a finding. The two findings this money
 * does hold are both invisible in a list, and one of them is invisible in a
 * pie as well.
 *
 * **Inaugural money is a pulse, not a flow.** Nothing in 2022, nothing in
 * 2023, nothing in 2026, and tens of millions in the years either side of an
 * inauguration. The empty years are the disclosure. That is why this is a
 * chart over time rather than a pie: a pie of three slices restates the
 * three cards above it and cannot say when any of it happened.
 *
 * **A category can be one cheque.** $50m of the $78.9m ever disclosed to
 * presidential libraries is a single line from a single filer, which is 63%
 * of the category. "47 donors gave $78.9m" is true and leaves a reader with
 * a picture of an industry habit rather than of one decision.
 *
 * A third thing, which is not in this data at all and has to be carried
 * beside it: the whole $162m is an eighth of what the same forms sent to
 * members of Congress directly. Without it the largest number on the site
 * sits next to nothing that gives it a size.
 *
 * Drawn as plain SVG. It is a bar chart over six years, and reaching for a
 * charting library to place eighteen rectangles would add a dependency to
 * the bundle that this file is shorter than.
 */

import { type OtherKind, type OtherMoney, OTHER_KINDS } from "../api";
import { ORDER, columns, listed, silentYears } from "./around";
import { brief } from "./party";
import { WhereItWent } from "./WhereItWent";

/** One colour per kind, in the order the legend and the stack use. */
const TINTS: Record<OtherKind, { bar: string; dot: string }> = {
  ple: { bar: "fill-brand", dot: "bg-brand" },
  pic: { bar: "fill-brand/55", dot: "bg-brand/55" },
  me: { bar: "fill-brand/25", dot: "bg-brand/25" },
};

/** A category is carried by one cheque at this share of it or more. Below
 *  it, naming the largest line would be the same overstatement in the other
 *  direction: 7% is not a concentration. */
const CARRIED = 0.4;

const HEIGHT = 150;
const GAP = 14;

function ByYear({ data }: { data: OtherMoney }) {
  const cols = columns(data);
  if (cols.length === 0) return null;
  const peak = Math.max(...cols.map((c) => c.total), 1);
  const width = cols.length * 100;

  return (
    <figure className="mt-6">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT + 34}`}
        // Width drives the height, rather than a fixed height letterboxing
        // the drawing into the middle of a wide container.
        className="w-full"
        role="img"
        aria-label="Disclosed money by year, split by what it was for"
      >
        {cols.map((col, i) => {
          const x = i * 100 + GAP;
          const w = 100 - GAP * 2;
          // Stacked from the baseline up, largest category first, so the
          // eye reads the same order as the legend.
          let y = HEIGHT;
          return (
            <g key={col.year}>
              {col.parts.map((part) => {
                const h = (part.total / peak) * (HEIGHT - 10);
                y -= h;
                return (
                  <rect
                    key={part.kind}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    className={TINTS[part.kind].bar}
                  />
                );
              })}
              {/* A year with nothing gets a baseline rule and a word, not an
                  empty space. An absence a reader cannot see is an absence
                  they will read as an omission by us. */}
              {col.total === 0 && (
                <>
                  <line
                    x1={x}
                    y1={HEIGHT}
                    x2={x + w}
                    y2={HEIGHT}
                    className="stroke-line"
                    strokeWidth={2}
                  />
                  <text
                    x={x + w / 2}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    className="fill-faint text-[11px]"
                  >
                    nothing
                  </text>
                </>
              )}
              <text
                x={x + w / 2}
                y={HEIGHT + 18}
                textAnchor="middle"
                className="fill-muted text-[13px]"
              >
                {col.year}
              </text>
              {col.total > 0 && (
                <text
                  x={x + w / 2}
                  y={HEIGHT + 32}
                  textAnchor="middle"
                  className="fill-faint text-[11px]"
                >
                  {brief(col.total)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted">
        {ORDER.map((kind) => (
          <span key={kind} className="inline-flex items-center gap-2">
            <span className={`inline-block h-3 w-3 rounded-sm ${TINTS[kind].dot}`} />
            {OTHER_KINDS[kind].label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/** The pulse, in a sentence. See `silentYears` for why it is not left to
 *  the geometry: a zero segment in a stack is invisible. */
function ThePulse({ data }: { data: OtherMoney }) {
  const pulsed = ORDER.map((kind) => ({ kind, silent: silentYears(data, kind) })).filter(
    (row) => row.silent.length > 0,
  );
  if (pulsed.length === 0) return null;
  return (
    <div className="mt-5 space-y-2">
      {pulsed.map(({ kind, silent }) => (
        <p key={kind} className="max-w-3xl text-sm leading-relaxed text-muted">
          <span className="font-medium text-ink">
            {OTHER_KINDS[kind].label} disclosed nothing at all in {listed(silent)}
          </span>
          . {kind === "pic"
            ? "It pays for staging an inauguration, so it arrives in the months around one and not otherwise — which is why a total for it describes an event rather than a habit."
            : "The gap is in the record, not in this page."}
        </p>
      ))}
    </div>
  );
}

/**
 * Where a category turns out to be one filer.
 *
 * Only where it genuinely is. Inaugural money is spread across 128 donors
 * with the largest at 7%, and calling that a concentration would be the
 * same failure as hiding the library one.
 */
function CarriedByOne({ data }: { data: OtherMoney }) {
  const carried = data.summary.filter(
    (row) => row.total > 0 && row.biggest / row.total >= CARRIED && row.biggest_honoree,
  );
  if (carried.length === 0) return null;
  return (
    <div className="mt-6 space-y-2">
      {carried.map((row) => (
        <p
          key={row.kind}
          className="max-w-3xl rounded-xl border border-line bg-sunk p-4 text-sm leading-relaxed text-muted"
        >
          <span className="font-medium text-ink">
            {brief(row.biggest)} of the {brief(row.total)} disclosed as{" "}
            {OTHER_KINDS[row.kind].label.toLowerCase()} is one cheque
          </span>
          , written to “{row.biggest_honoree}”. It is{" "}
          {Math.round((row.biggest / row.total) * 100)}% of everything{" "}
          {row.donors.toLocaleString()} organisations have ever disclosed under that heading, so the
          category total is closer to one decision than to a habit.
        </p>
      ))}
    </div>
  );
}

/**
 * The size of the whole thing.
 *
 * $162m has no scale on its own, and readers supply one — usually far too
 * large, because the individual cheques are eight figures and the legal
 * ceiling on what a member may accept is four. The same forms sent eight
 * times as much to members of Congress directly, and that is the sentence
 * that stops this page overstating itself.
 */
export function BesideCongress({ data }: { data: OtherMoney }) {
  if (data.around_them <= 0 || data.to_members <= 0) return null;
  const times = data.to_members / data.around_them;
  const share = (data.around_them / (data.around_them + data.to_members)) * 100;
  return (
    <div className="mt-8 rounded-2xl border border-line bg-card p-5 shadow-sm">
      <p className="max-w-3xl leading-relaxed">
        <span className="font-medium">
          All of it together is {brief(data.around_them)}. The same filers sent{" "}
          {brief(data.to_members)} to members of Congress by name
        </span>{" "}
        — {times < 10 ? Math.round(times) : Math.round(times / 10) * 10} times as much. The uncapped
        cheques are the ones worth reading about; they are not where the money is.
      </p>
      <div
        className="mt-4 flex h-4 overflow-hidden rounded-full bg-sunk"
        role="img"
        aria-label={`${brief(data.around_them)} spent around them against ${brief(data.to_members)} to members of Congress`}
      >
        <div className="bg-brand" style={{ width: `${share}%` }} />
        <div className="flex-1 bg-brand/25" />
      </div>
      <p className="mt-3 text-sm text-muted">
        <span className="font-medium text-ink">{Math.round(share)}%</span> of what these forms
        disclose is the money on this page. The rest{" "}
        <a href="#who-funds-congress" className="font-medium text-brand hover:underline">
          went to members of Congress
        </a>
        , a few thousand dollars at a time.
      </p>
    </div>
  );
}

/**
 * Everything above the cheques, in the order the questions get asked.
 *
 * Who got it, then when, then how few cheques it took. `BesideCongress` is
 * not here: it is a handoff to the section that follows on the page, and it
 * only works flush against it, so the page places it after the cheques
 * rather than this component burying it in the middle.
 */
export function AroundThem({ data }: { data: OtherMoney }) {
  return (
    <div>
      <WhereItWent data={data} />
      <ByYear data={data} />
      <ThePulse data={data} />
      <CarriedByOne data={data} />
    </div>
  );
}
